import { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";
import { getMovimentosGeral } from "../api";

// ─── DESIGN TOKENS (idêntico ao painel executivo) ────────────────────────────
const C = {
  crimson: "#C8003E", amber: "#F5A623", darkBg: "#000000ff",
  cardBg: "#1A1D24", border: "#2A2D35", text: "#FFFFFF",
  muted: "#8B90A0", danger: "#EF4444", success: "#22C55E", indigo: "#6366F1",
};

// ─── CONSTANTES DE NEGÓCIO ───────────────────────────────────────────────────
const GRUPO_VALIDO       = "CONTA_CORRENTE_PAG";
const FLUXO_EXCLUIDO     = "Não Considerada";
const CAMPO_FLUXO        = "Fluxo de Caixa";
const CAMPO_DATA_PAG     = "Data Pagamento";
const CAMPO_DATA_EMISSAO = "Data Emissão";       // com acento — igual à planilha
const CAMPO_VALOR_PAGO   = "Valor Pago";
const CAMPO_PRESTADOR    = "Prestador";           // campo exato da API
const CAMPO_CAT_SUPERIOR = "Categoria Superior"; // campo exato da API
const CAMPO_GRUPO        = "Grupo";

// ─── PARSERS ─────────────────────────────────────────────────────────────────
/**
 * Usa a mesma lógica do código de referência:
 * remove pontos de milhar, troca vírgula decimal por ponto.
 */
const parseBRL = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? null : n;
};

/**
 * Converte "dd/mm/yyyy" ou "yyyy-mm-dd" para objeto Date. Retorna null se inválido.
 */
const parseDate = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  // dd/mm/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    const d = new Date(`${yyyy}-${mm}-${dd}`);
    return isNaN(d) ? null : d;
  }
  // yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }
  return null;
};

/**
 * Diferença em dias entre duas datas. Retorna 0 se negativo (regra de negócio).
 */
const dateDiffDays = (dataPag, dataEmissao) => {
  if (!dataPag || !dataEmissao) return null;
  const diff = Math.round((dataPag - dataEmissao) / 86_400_000);
  return diff < 0 ? 0 : diff;
};

const toMonthKey = (d) => {
  if (!d) return null;
  const s = String(d).trim();
  const parts = s.split("/");
  if (parts.length === 3) return `${parts[1]}/${parts[2]}`;
  return null;
};

const monthLabel = (key) => {
  if (!key) return key;
  const [mm, yyyy] = key.split("/");
  const months = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const label = months[parseInt(mm, 10) - 1] || mm;
  return yyyy ? `${label} ${yyyy}` : label;
};

const fmt = (v) =>
  v == null ? "-" :
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const fmtShort = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} mi`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(0)} mil`;
  return fmt(v);
};

const fmtDias = (v) =>
  v == null ? "-" : `${Number(v).toFixed(2)}`;

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────
function Select({ label, options, value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>
      <label style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", color: C.text, textTransform: "uppercase" }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: C.cardBg, color: C.text, border: `1px solid ${C.border}`,
          borderRadius: 6, padding: "9px 36px 9px 12px", fontSize: 13,
          fontFamily: "inherit", appearance: "none", cursor: "pointer",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238B90A0' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
          outline: "none", minWidth: 200,
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function KpiCard({ label, value, sub, accent = false }) {
  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${accent ? C.crimson : C.border}`,
      borderRadius: 10, padding: "18px 24px", display: "flex", flexDirection: "column", gap: 6,
      boxShadow: accent ? `0 0 28px ${C.crimson}33` : "none",
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: C.muted, textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: 28, fontWeight: 800, color: accent ? C.crimson : C.text, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{value}</span>
      {sub && <span style={{ fontSize: 11, color: C.muted }}>{sub}</span>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 style={{ margin: "0 0 18px 0", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: C.muted, textTransform: "uppercase" }}>
      {children}
    </h3>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, gap: 16 }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.crimson}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <span style={{ color: C.muted, fontSize: 13 }}>Carregando dados…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{ background: "#2A1015", border: `1px solid ${C.danger}55`, borderRadius: 10, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 24 }}>
      <span style={{ color: C.danger, fontSize: 13 }}>⚠️ {message}</span>
      <button onClick={onRetry} style={{ background: C.crimson, color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Tentar novamente</button>
    </div>
  );
}

function TooltipPrazo({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#1E2128", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
      <p style={{ margin: "0 0 6px 0", fontSize: 11, color: C.muted }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ margin: "2px 0", fontSize: 13, color: p.color, fontWeight: 600 }}>
          {p.name}: {Number(p.value).toFixed(2)} dias
        </p>
      ))}
    </div>
  );
}

function TooltipSaidas({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#1E2128", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
      <p style={{ margin: "0 0 6px 0", fontSize: 11, color: C.muted }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ margin: "2px 0", fontSize: 13, color: p.color, fontWeight: 600 }}>
          {p.name}: R$ {fmtShort(p.value)}
        </p>
      ))}
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function PrazoPagamentoDash() {
  const [dadosRaw, setDadosRaw] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [erro,     setErro]     = useState(null);

  // Filtros externos (header)
  const [filtPrestador, setFiltPrestador] = useState("all");
  const [filtCategoria, setFiltCategoria] = useState("all");
  const [filtAno,       setFiltAno]       = useState("all");
  const [filtMes,       setFiltMes]       = useState("all");

  const navigate = useNavigate();

  const getDataAtualizacao = () => {
    const agora = new Date();
    if (agora.getHours() < 12) agora.setDate(agora.getDate() - 1);
    return `${agora.toLocaleDateString("pt-BR")} - 12:00`;
  };

  // ── Fetch — padrão do código de referência ────────────────────────────────
  const fetchDados = () => {
    setLoading(true);
    setErro(null);
    getMovimentosGeral()
      .then(res => {
        const data = Array.isArray(res) ? res : res.data || [];
        setDadosRaw(data);
        setLoading(false);
      })
      .catch(err => {
        setErro(err.message);
        setLoading(false);
      });
  };

  useEffect(() => { fetchDados(); }, []);

  // ── Dados base: filtros fixos de negócio + processamento de datas/diff ────
  const dadosBase = useMemo(() =>
    dadosRaw
      .filter(m => {
        if (!parseDate(m[CAMPO_DATA_PAG]))             return false; // data pag. nula
        if (m[CAMPO_FLUXO]  === FLUXO_EXCLUIDO)        return false; // fluxo excluído
        if (m[CAMPO_GRUPO]  !== GRUPO_VALIDO)           return false; // somente CONTA_CORRENTE_PAG
        return true;
      })
      .map(m => {
        const dp  = parseDate(m[CAMPO_DATA_PAG]);
        const de  = parseDate(m[CAMPO_DATA_EMISSAO]);
        const val = parseBRL(m[CAMPO_VALOR_PAGO]) ?? 0;
        const diffDias = (dp && de) ? dateDiffDays(dp, de) ?? 0 : 0;
        return { ...m, _dp: dp, _diffDias: diffDias, _valorPago: val };
      }),
    [dadosRaw]
  );

  // ── Opções dos filtros externos (geradas a partir dos dados já filtrados pela base) ──
  const prestadorOpcoes = useMemo(() => {
    const u = [...new Set(dadosBase.map(r => r[CAMPO_PRESTADOR]).filter(Boolean))].sort();
    return [{ value: "all", label: "Todos os Prestadores" }, ...u.map(v => ({ value: v, label: v }))];
  }, [dadosBase]);

  const categoriaOpcoes = useMemo(() => {
    const u = [...new Set(dadosBase.map(r => r[CAMPO_CAT_SUPERIOR]).filter(Boolean))].sort();
    return [{ value: "all", label: "Todas as Categorias" }, ...u.map(v => ({ value: v, label: v }))];
  }, [dadosBase]);

  const anoOpcoes = useMemo(() => {
    const anos = [...new Set(
      dadosBase.map(r => {
        const k = toMonthKey(r[CAMPO_DATA_PAG]);
        return k ? k.split("/")[1] : null;
      }).filter(Boolean)
    )].sort();
    return [{ value: "all", label: "Todos os Anos" }, ...anos.map(a => ({ value: a, label: a }))];
  }, [dadosBase]);

  const mesOpcoes = useMemo(() => {
    const keys = [...new Set(dadosBase.map(r => toMonthKey(r[CAMPO_DATA_PAG])).filter(Boolean))]
      .sort((a, b) => {
        const [ma, ya] = a.split("/").map(Number);
        const [mb, yb] = b.split("/").map(Number);
        return ya !== yb ? ya - yb : ma - mb;
      });
    return [{ value: "all", label: "Todos os Meses" }, ...keys.map(k => ({ value: k, label: monthLabel(k) }))];
  }, [dadosBase]);

  // ── Aplicar filtros externos ──────────────────────────────────────────────
  const dadosFiltrados = useMemo(() =>
    dadosBase.filter(r => {
      if (filtPrestador !== "all" && r[CAMPO_PRESTADOR] !== filtPrestador) return false;
      if (filtCategoria !== "all" && r[CAMPO_CAT_SUPERIOR] !== filtCategoria) return false;
      const mk = toMonthKey(r[CAMPO_DATA_PAG]);
      if (filtAno !== "all" && (!mk || mk.split("/")[1] !== filtAno)) return false;
      if (filtMes !== "all" && mk !== filtMes) return false;
      return true;
    }),
    [dadosBase, filtPrestador, filtCategoria, filtAno, filtMes]
  );

  // ── KPI principal — usa valores pré-computados no dadosBase ─────────────
  const prazoMedioTotal = useMemo(() => {
    const sumProd  = dadosFiltrados.reduce((s, r) => s + (r._diffDias * r._valorPago), 0);
    const sumValor = dadosFiltrados.reduce((s, r) => s + r._valorPago, 0);
    return sumValor > 0 ? sumProd / sumValor : null;
  }, [dadosFiltrados]);

  const totalValorPago = useMemo(() =>
    dadosFiltrados.reduce((s, r) => s + r._valorPago, 0),
    [dadosFiltrados]
  );

  // ── Tabela: Detalhamento por Categoria Superior (campo da planilha) ───────
  const tabelaGrupos = useMemo(() => {
    const map = {};
    dadosFiltrados.forEach(r => {
      const grupo = r[CAMPO_CAT_SUPERIOR] || "Outros";
      if (!map[grupo]) map[grupo] = { sumProd: 0, sumValor: 0 };
      map[grupo].sumProd  += r._diffDias * r._valorPago;
      map[grupo].sumValor += r._valorPago;
    });
    return Object.entries(map)
      .map(([grupo, { sumProd, sumValor }]) => ({
        grupo,
        prazoMedio: sumValor > 0 ? sumProd / sumValor : null,
        valorPago:  sumValor,
      }))
      .sort((a, b) => (b.valorPago || 0) - (a.valorPago || 0));
  }, [dadosFiltrados]);

  // ── Gráfico 1: Prazo Médio por Mês ───────────────────────────────────────
  const graficoPrazo = useMemo(() => {
    const map = {};
    dadosFiltrados.forEach(r => {
      const mk = toMonthKey(r[CAMPO_DATA_PAG]);
      if (!mk) return;
      if (!map[mk]) map[mk] = { sumProd: 0, sumValor: 0 };
      map[mk].sumProd  += r._diffDias * r._valorPago;
      map[mk].sumValor += r._valorPago;
    });
    return Object.entries(map)
      .sort((a, b) => {
        const [ma, ya] = a[0].split("/").map(Number);
        const [mb, yb] = b[0].split("/").map(Number);
        return ya !== yb ? ya - yb : ma - mb;
      })
      .map(([mk, { sumProd, sumValor }]) => ({
        mes: monthLabel(mk),
        "Prazo Médio": sumValor > 0 ? parseFloat((sumProd / sumValor).toFixed(2)) : null,
      }));
  }, [dadosFiltrados]);

  // ── Gráfico 2: Saídas por Mês (Valor Pago) ───────────────────────────────
  const graficoSaidas = useMemo(() => {
    const map = {};
    dadosFiltrados.forEach(r => {
      const mk = toMonthKey(r[CAMPO_DATA_PAG]);
      if (!mk) return;
      map[mk] = (map[mk] || 0) + r._valorPago;
    });
    return Object.entries(map)
      .sort((a, b) => {
        const [ma, ya] = a[0].split("/").map(Number);
        const [mb, yb] = b[0].split("/").map(Number);
        return ya !== yb ? ya - yb : ma - mb;
      })
      .map(([mk, total]) => ({
        mes: monthLabel(mk),
        "Valor Pago": parseFloat(total.toFixed(2)),
      }));
  }, [dadosFiltrados]);

  const isInitialLoad = loading && dadosRaw.length === 0;

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ background: C.darkBg, minHeight: "100vh", fontFamily: "'DM Sans','Segoe UI',sans-serif", color: C.text }}>

      {/* ── Header ── */}
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center" style={{ flexWrap: "wrap", gap: 12 }}>
        <div className="flex items-center space-x-2">
          <div>
            <span className="text-2xl font-bold">YOUX</span>
            <span className="text-xl font-light">GROUP</span>
            <h1 className="text-purple-200 text-[10px] uppercase tracking-wider">
              Atualizado em {getDataAtualizacao()}
            </h1>
          </div>
        </div>

        {/* Filtros */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Select label="Prestador de Serviço" options={prestadorOpcoes} value={filtPrestador} onChange={setFiltPrestador} />
          <Select label="Nome Categoria Sup."  options={categoriaOpcoes} value={filtCategoria} onChange={setFiltCategoria} />
          <Select label="Ano"                  options={anoOpcoes}        value={filtAno}        onChange={setFiltAno} />
          <Select label="Mês"                  options={mesOpcoes}        value={filtMes}        onChange={setFiltMes} />
        </div>

        <button
          onClick={() => navigate("/inicio")}
          className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
        >
          <Menu className="w-4 h-4" />
          Menu Principal
        </button>
      </header>

      {/* ── Conteúdo ── */}
      <div style={{ padding: "32px 40px", maxWidth: 1400, margin: "0 auto" }}>

        {erro && <ErrorBanner message={erro} onRetry={fetchDados} />}

        {isInitialLoad ? <Spinner /> : (
          <>
            {/* ── Título ── */}
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>
                Indicadores — Prazo de Pagamento
              </h2>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
                Movimentos Geral · Grupo: {GRUPO_VALIDO} · {dadosFiltrados.length} registros
              </p>
            </div>

            {/* ── KPI Principal ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 36 }}>
              <KpiCard
                label="Prazo Médio de Pagamento Total"
                value={prazoMedioTotal != null ? `${fmtDias(prazoMedioTotal)} dias` : "-"}
                sub="Média ponderada pelo Valor Pago"
                accent
              />
              <KpiCard
                label="Total Valor Pago"
                value={`R$ ${fmtShort(totalValorPago)}`}
                sub={`R$ ${fmt(totalValorPago)}`}
              />
              <KpiCard
                label="Grupos de Gastos"
                value={tabelaGrupos.length}
                sub="com lançamentos no período"
              />
            </div>

            {/* ── Layout dois painéis: tabela + gráficos ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}>

              {/* Coluna esquerda: Tabela detalhamento por grupo */}
              <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "20px 20px 14px" }}>
                  <SectionTitle>Detalhamento por Grupo de Gastos</SectionTitle>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}`, background: "#14161C" }}>
                      {["Grupo de Gastos", "Prazo Médio", "Valor (R$)"].map((col, i) => (
                        <th key={col} style={{
                          padding: "11px 16px",
                          textAlign: i === 0 ? "left" : "right",
                          fontSize: 10, fontWeight: 700,
                          letterSpacing: "0.12em", color: C.muted, textTransform: "uppercase",
                        }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tabelaGrupos.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ padding: "40px 20px", textAlign: "center", color: C.muted, fontSize: 13 }}>
                          Nenhum dado para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      <>
                        {tabelaGrupos.map((row, i) => (
                          <tr
                            key={row.grupo}
                            style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : "#14161C88", transition: "background 0.12s" }}
                            onMouseEnter={e => e.currentTarget.style.background = `${C.crimson}18`}
                            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : "#14161C88"}
                          >
                            <td style={{ padding: "10px 16px", color: C.text, fontWeight: 500 }}>{row.grupo}</td>
                            <td style={{ padding: "10px 16px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: C.amber, fontWeight: 700 }}>
                              {row.prazoMedio != null ? `${fmtDias(row.prazoMedio)}` : "-"}
                            </td>
                            <td style={{ padding: "10px 16px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: C.text }}>
                              {fmt(row.valorPago)}
                            </td>
                          </tr>
                        ))}
                        {/* Total */}
                        <tr style={{ borderTop: `2px solid ${C.border}`, background: "#0D0F14" }}>
                          <td style={{ padding: "12px 16px", fontWeight: 800, color: C.text }}>Total geral</td>
                          <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, color: C.crimson, fontVariantNumeric: "tabular-nums" }}>
                            {prazoMedioTotal != null ? fmtDias(prazoMedioTotal) : "-"}
                          </td>
                          <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: C.text }}>
                            {fmt(totalValorPago)}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Coluna direita: dois gráficos empilhados */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                {/* Gráfico 1: Prazo Médio por Mês */}
                <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 20px 12px" }}>
                  <SectionTitle>Prazo Médio (Mês)</SectionTitle>
                  {graficoPrazo.length === 0
                    ? <div style={{ textAlign: "center", padding: 48, color: C.muted, fontSize: 13 }}>Sem dados.</div>
                    : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={graficoPrazo} margin={{ top: 4, right: 8, left: 0, bottom: 60 }} barCategoryGap="30%">
                          <CartesianGrid vertical={false} stroke={C.border} />
                          <XAxis dataKey="mes" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                          <YAxis tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} width={36} tickFormatter={v => `${v}d`} />
                          {prazoMedioTotal != null && (
                            <ReferenceLine
                              y={prazoMedioTotal}
                              stroke={C.amber}
                              strokeDasharray="5 4"
                              label={{ value: `Média ${fmtDias(prazoMedioTotal)}d`, fill: C.amber, fontSize: 10, position: "insideTopRight" }}
                            />
                          )}
                          <Tooltip content={<TooltipPrazo />} cursor={{ fill: "#FFFFFF08" }} />
                          <Bar dataKey="Prazo Médio" fill={C.crimson} radius={[3, 3, 0, 0]} maxBarSize={48} />
                        </BarChart>
                      </ResponsiveContainer>
                    )
                  }
                </div>

                {/* Gráfico 2: Saídas por Mês */}
                <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 20px 12px" }}>
                  <SectionTitle>Saídas Mês (R$)</SectionTitle>
                  {graficoSaidas.length === 0
                    ? <div style={{ textAlign: "center", padding: 48, color: C.muted, fontSize: 13 }}>Sem dados.</div>
                    : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={graficoSaidas} margin={{ top: 4, right: 8, left: 0, bottom: 60 }} barCategoryGap="30%">
                          <CartesianGrid vertical={false} stroke={C.border} />
                          <XAxis dataKey="mes" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                          <YAxis tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} width={52} tickFormatter={fmtShort} />
                          <Tooltip content={<TooltipSaidas />} cursor={{ fill: "#FFFFFF08" }} />
                          <Bar dataKey="Valor Pago" fill={C.crimson} radius={[3, 3, 0, 0]} maxBarSize={48} />
                        </BarChart>
                      </ResponsiveContainer>
                    )
                  }
                </div>

              </div>
            </div>

            {/* Rodapé de metadados */}
            <p style={{ marginTop: 16, fontSize: 11, color: C.muted, textAlign: "right" }}>
              {dadosFiltrados.length} registros filtrados · {dadosBase.length} após regras base · {dadosRaw.length} total
            </p>
          </>
        )}
      </div>
    </div>
  );
}